import { createBrowserHistory, History } from 'history';
import { getPid, getProductId } from '@/infrastructure/product-info';

export const BASE_NAME = '/v2';
export const H5_BASE_NAME = `${BASE_NAME}/h5`;

const histories = new Map<string, History>();

const getBaseName = (isH5 = false) => (isH5 ? H5_BASE_NAME : BASE_NAME);

export const isH5EntryPath = (pathname?: string) => {
  const currentPath = pathname ?? window?.location?.pathname ?? '';
  return Boolean(currentPath.startsWith(H5_BASE_NAME));
};

export type PlanStringObject = {[key: string]: string | number};

export function setQueryToCurrentUrl(params: PlanStringObject) {
  const { URL } = typeof window !== 'undefined' ? window : ({} as any);
  const url = new URL(window?.location?.href);

  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        url.searchParams.delete(key);
        value.forEach((valueItem) => {
          url.searchParams.append(key, valueItem);
        });
      } else if (typeof value === 'object') {
        url.searchParams.set(key, JSON.stringify(value));
      } else {
        url.searchParams.set(key, value);
      }
    } else {
      url.searchParams.delete(key);
    }
  });
  return url;
}

export function getHistory(isH5 = false) {
  const basename = getBaseName(isH5);
  if (!histories.has(basename)) {
    histories.set(basename, createBrowserHistory({ basename }));
  }
  return histories.get(basename)!;
}

export function pushHistory(
  history: History,
  pathname: string,
  search?: {
    [key: string]: string;
  },
) {
  history.push({
    pathname,
    search: toSearchStr(search),
  });
}

export function replaceHistory(
  history: History,
  pathname: string,
  search?: {
    [key: string]: string | number | undefined;
  },
) {
  history.replace({
    pathname,
    search: toSearchStr(search),
  });
}

export function toSearchStr(search?: {
  [key: string]: any;
}) {
  const productId = getProductId();
  const pid = getPid();
  const idParam = `productId=${productId}&pid=${pid}`;
  const optParam = search ? Object.keys(search)
    .map((key) => {
      if (key === 'productId' || key === 'pid') {
        return '';
      }
      if (search[key] === undefined || search[key] === '' || search[key] === null) {
        return '';
      }
      return `&${key}=${search[key]}`;
    })
    .join('') : '';
  return `${idParam}${optParam}`;
}

export function toSearchObj(search: string) {
  const searchObj: {
    [key: string]: any,
  } = {};
  const searchParams = /&quot;/.test(search) ? search.replace(/&quot;/gi, '"') : search;
  new URLSearchParams(searchParams).forEach((value, key) => {
    searchObj[key] = value;
  });
  return searchObj;
}

export function getParamFromUrl(param: string) {
  const { search } = window.location;
  const params = new URLSearchParams(search);
  return params.get(param) || '';
}

export function getNumberParamFromUrl(param: string, defaultValue = 0): number {
  const { search } = window.location;
  const params = new URLSearchParams(search);
  const val = params.get(param);
  return val ? Number(val) : defaultValue;
}

export function getParamFromUrlBatch(params: string[]): string[] {
  const { search } = window.location;
  const urlParams = new URLSearchParams(search);
  return params.map(param => urlParams.get(param) || '');
}
